import { Helmet } from 'react-helmet-async';
import { useState, useCallback, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTheme } from '@mui/material/styles';
import { Box, Container, IconButton, MenuItem, Popover, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, Toolbar } from '@mui/x-data-grid';

import Iconify from '../../components/iconify';
import { ToolBarSearch, ToolBarColumnToggler, Container as DataGridContainer } from '../../components/datagrid';
import { useGetUsersQuery } from '../../slices/apis/userApi';
import type { UserProps } from '../../models';

type DataGridRow = {
	id: UserProps['id'];
	firstname: UserProps['firstname'];
	lastname: UserProps['lastname'];
	phone: UserProps['phone'];
	email: UserProps['email'];
	company: UserProps['company'];
	role: string;
};

export default function Users() {
	const [menuOpen, setMenuOpen] = useState<HTMLButtonElement | null>(null);
	const [selectedUserId, setSelectedUserId] = useState<string | number | null>(null);

	const navigate = useNavigate();
	const theme = useTheme();
	const { data = [], isFetching } = useGetUsersQuery();

	const handleOpenMenu = useCallback((e: MouseEvent<HTMLButtonElement>) => {
		setMenuOpen(e.currentTarget);
		setSelectedUserId(e.currentTarget.getAttribute('data-user-id'));
	}, []);

	const handleCloseMenu = () => {
		setMenuOpen(null);
	};

	const handleEditUser = () => {
		navigate(`/dashboard/admin/users/${selectedUserId}/edit`);
		setMenuOpen(null);
	};

	return (
		<>
			<Helmet>
				<title>Users - {process.env.REACT_APP_ORG}</title>
			</Helmet>
			<Container maxWidth={false} sx={{ width: '90%', height: '100%', display: 'flex', flexDirection: 'column' }}>
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
					<Typography variant="landing_page_heading" gutterBottom>
						Users
					</Typography>
				</Stack>

				<DataGridContainer>
					<DataGrid
						disableColumnFilter
						disableDensitySelector
						disableColumnSelector
						showToolbar
						loading={isFetching}
						columnHeaderHeight={48}
						initialState={{
							sorting: {
								sortModel: [{ field: 'id', sort: 'desc' }],
							},
							pagination: {
								paginationModel: { pageSize: 50 },
							},
						}}
						pageSizeOptions={[25, 50, 75]}
						slots={{
							toolbar: () => (
								<Toolbar>
									<Box sx={{ pl: 1 }}>
										<ToolBarSearch />
									</Box>
									<Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
										<Tooltip title="Columns">
											<ToolBarColumnToggler />
										</Tooltip>
									</Box>
								</Toolbar>
							),
						}}
						sx={{
							width: '100%',
							borderRadius: 3,
							'& .MuiDataGrid-columnHeaderTitle': {
								whiteSpace: 'normal',
								lineHeight: 1.4,
								fontSize: '0.9rem',
								color: 'grey',
							},
							'& .MuiDataGrid-columnHeader': {
								backgroundColor: theme.palette.grey[100],
							},
						}}
						columns={[
							{
								field: 'id',
								headerName: 'ID',
								resizable: false,
								flex: 0.25,
							},
							{
								field: 'firstname',
								headerName: 'First Name',
								resizable: false,
								flex: 0.7,
							},
							{
								field: 'lastname',
								headerName: 'Last Name',
								resizable: false,
								flex: 0.7,
							},
							{
								field: 'phone',
								headerName: 'Phone',
								resizable: false,
								flex: 0.7,
							},
							{
								field: 'email',
								headerName: 'Email',
								resizable: false,
								flex: 1.1,
							},
							{
								field: 'company',
								headerName: 'Company',
								resizable: false,
								flex: 1,
							},
							{
								field: 'role',
								headerName: 'Role',
								resizable: false,
								flex: 0.6,
							},
							{
								field: 'actions',
								headerName: '',
								headerAlign: 'center',
								align: 'right',
								flex: 0.3,
								sortable: false,
								filterable: false,
								resizable: false,
								renderCell: (params) => {
									const {
										row: { id },
									} = params;

									return (
										<IconButton size="large" color="inherit" data-user-id={id} onClick={handleOpenMenu}>
											<Iconify icon={'eva:more-vertical-fill'} />
										</IconButton>
									);
								},
							},
						]}
						rows={data.map((user) => {
							return {
								id: user.id,
								firstname: user.firstname,
								lastname: user.lastname,
								phone: user.phone,
								email: user.email,
								company: user.company,
								role: user.roles?.[0]?.role ?? '',
								actions: '',
							} as DataGridRow;
						})}
					/>
				</DataGridContainer>
			</Container>

			<Popover
				open={Boolean(menuOpen)}
				anchorEl={menuOpen}
				onClose={handleCloseMenu}
				anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				PaperProps={{
					sx: {
						p: 1,
						width: 140,
						'& .MuiMenuItem-root': {
							px: 1,
							typography: 'body2',
							borderRadius: 0.75,
						},
					},
				}}
			>
				<MenuItem onClick={handleEditUser}>
					<Iconify icon={'eva:edit-fill'} sx={{ mr: 1 }} />
					View/Edit
				</MenuItem>
			</Popover>
		</>
	);
}
